import { PhxToastService } from 'phx-core';

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, CanActivate, Data, CanActivateChild } from '@angular/router';

import { Session } from '../services/session';
import { StatsigService } from '@services/statsig.service';

type StrategyType = 'some' | 'every';

interface IFeature {
  name: string;
}

interface IFeatureAccessGuard {
  name: string;
  features: IFeature[];
  strategy: StrategyType;
  redirectTo?: string;
  forceAllowSuperAdmin: string;
  enforcmentByFeatureGate?: string;
  toastMessageOnRedirect?: string;
}

@Injectable()
export class FeatureAccessGuard implements CanActivate, CanActivateChild {
  constructor(
    private router: Router,
    private session: Session,
    private statsigService: StatsigService,
    private phxToastService: PhxToastService,
  ) { }

  public canActivate(route: ActivatedRouteSnapshot): boolean {
    return this.checkForActivate(route);
  }

  public canLoad(route: Route): boolean {
    return this.checkForActivate(route);
  }

  public canActivateChild(route: ActivatedRouteSnapshot): boolean {
    return this.checkForActivate(route);
  }

  private checkForActivate(route: ActivatedRouteSnapshot | Route): boolean {
    const session: Session = this.session;
    const data: Data = route.data;
    const config: IFeatureAccessGuard = data.featureAccessGuard;
    const featureName: string = config.name;
    const forceAllowSuperAdmin: boolean = config.forceAllowSuperAdmin && session.isAdmin();
    const redirectTo: string = config.redirectTo || '/';
    const shouldCheckForMulitpleFeatureAccess: boolean = !!config.features;

    let shouldCheckAccess = true;
    if (config.enforcmentByFeatureGate) {
      shouldCheckAccess = this.statsigService.evaluateGate(config.enforcmentByFeatureGate);
    }

    if (!shouldCheckAccess) return true;

    if (shouldCheckForMulitpleFeatureAccess) return this.checkForMultipleFeatures(config.features, config.strategy, redirectTo);

    const featureNotAccessible = session.isDispatcher() && !session.isFeatureAllowed(featureName) && !forceAllowSuperAdmin;

    if (featureNotAccessible) {
      if (config.toastMessageOnRedirect) this.phxToastService.info(config.toastMessageOnRedirect);
      this.router.navigate([redirectTo]);
      return false;
    } else {
      return true;
    }
  }

  private checkForMultipleFeatures(features: IFeature[], strategy: StrategyType, redirectTo?: string): boolean {
    const isAccessAllowed: boolean = features[strategy]((feature: IFeature): boolean => {
      return this.session.isFeatureAllowed(feature.name);
    });

    if (!isAccessAllowed && redirectTo) this.router.navigate([redirectTo]);
    return isAccessAllowed;
  }
}
